// Main-thread bridge to the bot. Uses the Web Worker when the platform has
// one; otherwise (tests, exotic embeds, or a worker that failed to load or
// crashed) searches in-process on a timer tick. Cancelling terminates the
// worker — the search has no yield points — and respawns it so the next
// think still works.
import { chooseMove } from './AiPlayer.js';
import { fenToPosition } from '../game/fen.js';
import { moveToUci } from '../game/uci.js';
import type { Difficulty } from '../settings/types.js';
import type { WorkerRequest, WorkerResponse } from './worker/protocol.js';

export interface Thinking {
  /** Resolves to the chosen UCI move (null when there is none), or rejects on a search error. */
  readonly promise: Promise<string | null>;
  readonly cancel: () => void;
}

interface Pending {
  readonly request: WorkerRequest;
  readonly settle: (out: string | null | Error) => void;
}

export class BotPlayer {
  private worker: Worker | null;
  private nextId = 0;
  private readonly pending = new Map<string, Pending>();
  /** In-process searches not yet started; cleared on cancel so a discarded search never runs. */
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();

  constructor() {
    this.worker = this.spawn();
  }

  private spawn(): Worker | null {
    if (typeof Worker === 'undefined') return null;
    try {
      const w = new Worker(new URL('./worker/chessAi.worker.ts', import.meta.url), { type: 'module' });
      w.onmessage = (e: MessageEvent<WorkerResponse>) => this.onResponse(e.data);
      w.onerror = () => this.abandonWorker();
      w.onmessageerror = () => this.abandonWorker();
      return w;
    } catch {
      return null;
    }
  }

  /**
   * The worker failed to load or crashed: nothing will ever post back for the
   * searches it holds. Drop to in-process mode for good and rerun them there,
   * so the game never waits on a reply that cannot come.
   */
  private abandonWorker(): void {
    this.worker?.terminate();
    this.worker = null;
    for (const { request } of this.pending.values()) this.runInProcess(request);
  }

  private runInProcess(req: WorkerRequest): void {
    this.clearTimer(req.id);
    this.timers.set(req.id, setTimeout(() => {
      this.timers.delete(req.id);
      try {
        const move = chooseMove(fenToPosition(req.fen), req.difficulty, Math.random, Date.now, req.budgetMs);
        this.onResponse({ kind: 'result', id: req.id, uci: move === null ? null : moveToUci(move) });
      } catch (err) {
        this.onResponse({ kind: 'error', id: req.id, message: err instanceof Error ? err.message : String(err) });
      }
    }, 0));
  }

  private clearTimer(id: string): void {
    const timer = this.timers.get(id);
    if (timer === undefined) return;
    clearTimeout(timer);
    this.timers.delete(id);
  }

  private onResponse(res: WorkerResponse): void {
    const entry = this.pending.get(res.id);
    if (entry === undefined) return;
    this.pending.delete(res.id);
    entry.settle(res.kind === 'result' ? res.uci : new Error(res.message));
  }

  think(fen: string, difficulty: Difficulty, budgetMs?: number): Thinking {
    const id = `t${this.nextId++}`;
    const request: WorkerRequest = { kind: 'search', id, fen, difficulty, ...(budgetMs === undefined ? {} : { budgetMs }) };
    const promise = new Promise<string | null>((resolve, reject) => {
      this.pending.set(id, { request, settle: (out) => (out instanceof Error ? reject(out) : resolve(out)) });
      if (this.worker !== null) this.worker.postMessage(request);
      else this.runInProcess(request);
    });
    const cancel = (): void => {
      if (!this.pending.has(id)) return;
      this.pending.delete(id);
      this.clearTimer(id);
      if (this.worker === null) return;
      this.worker.terminate();
      this.worker = this.spawn();
      // Other searches were in the terminated worker too; restart them.
      for (const { request: other } of this.pending.values()) {
        if (this.worker !== null) this.worker.postMessage(other);
        else this.runInProcess(other);
      }
    };
    return { promise, cancel };
  }

  dispose(): void {
    for (const timer of this.timers.values()) clearTimeout(timer);
    this.timers.clear();
    this.pending.clear();
    this.worker?.terminate();
    this.worker = null;
  }
}
